const DEFAULT_DETAILS = {
  invalid_request: {
    title: 'Unexpected URL',
    message:
      "You took an unexpected route to the app. The button below will take you back to the site. If you're using a saved link or a bookmark, you may want to update it after you arrive.",
    buttonText: "Let's go!",
  },
  access_denied: {
    title: 'Invite Required',
    message:
      'Project Pilot is currently invite-only. The access list is email based. Double check that the email address you used is correct. If you believe this is an error, please reach out to request access.',
    buttonText: 'Logout',
  },
  generic: {
    title: 'Unexpected Error',
    message:
      'An unexpected error occurred. Please try again later. If the problem persists, please let us know.',
    buttonText: 'Try again',
  },
}

/**
 * showModal – a utility function to show a modal
 * for edge cases when styles/states/ui elements may not be displayed
 * (server errors, auth errors, etc.)
 *
 * All styles and dependencies are self-contained within this function.
 *
 * @param {string} id  The id of the modal
 * @param {string} title  The title of the modal
 * @param {string} message  The message to display in the modal
 * @param {string} buttonText  The text of the button to reload the page
 */
export function showModal(details) {
  const search = Object.fromEntries(new URLSearchParams(window.location.search))

  console.error(
    '[showModal] details →',
    JSON.stringify({ details, search }, null, 2),
  )
  let id = details?.id || search?.error || 'generic'
  if (!DEFAULT_DETAILS[id]) {
    id = 'generic'
  }
  const title = details?.title || DEFAULT_DETAILS[id]?.title
  const message = details?.message || DEFAULT_DETAILS[id]?.message
  const buttonText = details?.buttonText || DEFAULT_DETAILS[id]?.buttonText
  const email = details?.email

  // Send error to Sentry
  if (typeof window !== 'undefined' && window.Sentry) {
    window.Sentry.captureMessage(`Modal shown: ${id}`, {
      level: id === 'generic' ? 'error' : 'warning',
      tags: {
        error_type: id,
        source: 'showModal',
        modal_id: id,
      },
      extra: {
        id,
        title,
        message,
        buttonText,
        email,
        details,
        search_params: search,
        url: window.location.href,
      },
    })
  }

  // Inject Google Fonts if not already loaded
  if (!document.querySelector('link[href*="Inter"]')) {
    const fontLink = document.createElement('link')
    fontLink.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap'
    fontLink.rel = 'stylesheet'
    document.head.appendChild(fontLink)
  }

  // Remove any existing modal with the same id
  const existing = document.getElementById(id)
  if (existing) existing.remove()

  // Create modal container
  const modal = document.createElement('div')
  modal.id = id

  // Overlay styles
  Object.assign(modal.style, {
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100vw',
    height: '100svh',
    background: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '9999',
    fontFamily:
      "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    opacity: '0',
    transition: 'opacity 0.2s ease-in-out',
  })

  // Create modal content
  const modalContent = document.createElement('div')
  Object.assign(modalContent.style, {
    background: 'white',
    padding: '2.5rem',
    borderRadius: '16px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 0 1px rgba(0, 0, 0, 0.1)',
    textAlign: 'center',
    maxWidth: '90vw',
    width: '440px',
    transform: 'scale(0.95)',
    transition: 'transform 0.2s ease-in-out',
  })
  if (id === 'invalid_request') {
    const iconContainer = document.createElement('div')
    Object.assign(iconContainer.style, {
      width: '64px',
      height: '64px',
      margin: '0 auto 1.5rem',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 16px rgba(59, 130, 246, 0.3)',
    })

    // Create SVG compass/navigation icon
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', '32')
    svg.setAttribute('height', '32')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('stroke', 'white')
    svg.setAttribute('stroke-width', '2')
    svg.setAttribute('stroke-linecap', 'round')
    svg.setAttribute('stroke-linejoin', 'round')

    // Compass circle
    const circle = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'circle',
    )
    circle.setAttribute('cx', '12')
    circle.setAttribute('cy', '12')
    circle.setAttribute('r', '10')

    // Compass needle pointing up
    const needle = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    )
    needle.setAttribute('d', 'M12 2v8M12 14v8M2 12h8M14 12h8')

    svg.appendChild(circle)
    svg.appendChild(needle)
    iconContainer.appendChild(svg)
    modalContent.appendChild(iconContainer)
  } else if (id === 'access_denied') {
    const iconContainer = document.createElement('div')
    Object.assign(iconContainer.style, {
      width: '56px',
      height: '56px',
      margin: '0 auto 1.5rem',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
    })

    // Create SVG lock icon
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', '28')
    svg.setAttribute('height', '28')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('stroke', 'white')
    svg.setAttribute('stroke-width', '2')
    svg.setAttribute('stroke-linecap', 'round')
    svg.setAttribute('stroke-linejoin', 'round')

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    rect.setAttribute('x', '3')
    rect.setAttribute('y', '11')
    rect.setAttribute('width', '18')
    rect.setAttribute('height', '11')
    rect.setAttribute('rx', '2')
    rect.setAttribute('ry', '2')

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', 'M7 11V7a5 5 0 0 1 10 0v4')

    svg.appendChild(rect)
    svg.appendChild(path)
    iconContainer.appendChild(svg)
    modalContent.appendChild(iconContainer)
  } else if (id === 'generic') {
    const iconContainer = document.createElement('div')
    Object.assign(iconContainer.style, {
      width: '56px',
      height: '56px',
      margin: '0 auto 1.5rem',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
    })

    // Create SVG lock icon
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', '28')
    svg.setAttribute('height', '28')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('stroke', 'white')
    svg.setAttribute('stroke-width', '2')
    svg.setAttribute('stroke-linecap', 'round')
    svg.setAttribute('stroke-linejoin', 'round')

    const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect')
    rect.setAttribute('x', '3')
    rect.setAttribute('y', '11')
    rect.setAttribute('width', '18')
    rect.setAttribute('height', '11')
    rect.setAttribute('rx', '2')
    rect.setAttribute('ry', '2')

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', 'M7 11V7a5 5 0 0 1 10 0v4')

    svg.appendChild(rect)
    svg.appendChild(path)
    iconContainer.appendChild(svg)
    modalContent.appendChild(iconContainer)
  }

  // Create title
  const titleElement = document.createElement('h2')
  titleElement.textContent = title
  Object.assign(titleElement.style, {
    margin: '0 0 1rem 0',
    fontSize: '1.5rem',
    fontWeight: '600',
    color: '#1a1a1a',
    lineHeight: '1.3',
    letterSpacing: '-0.01em',
  })

  // Create message
  const messageElement = document.createElement('p')
  messageElement.textContent = message
  Object.assign(messageElement.style, {
    margin: '0 0 2rem 0',
    fontSize: '0.9375rem',
    color: '#666',
    lineHeight: '1.6',
    fontWeight: '400',
  })

  if (id === 'access_denied') {
    const emailContainer = document.createElement('div')
    Object.assign(emailContainer.style, {
      background: '#f8fafc',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      padding: '0.75rem 1rem',
      margin: '0 0 2rem 0',
      fontSize: '0.875rem',
      color: '#475569',
      fontFamily: "'SF Mono', 'Monaco', 'Courier New', monospace",
    })

    const emailLabel = document.createElement('div')
    emailLabel.textContent = 'Account:'
    Object.assign(emailLabel.style, {
      fontSize: '0.75rem',
      color: '#94a3b8',
      marginBottom: '0.25rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      fontWeight: '500',
    })

    const emailText = document.createElement('div')
    emailText.textContent = email
    Object.assign(emailText.style, {
      color: '#1e293b',
      fontWeight: '500',
    })

    if (email) {
      emailContainer.appendChild(emailLabel)
      emailContainer.appendChild(emailText)
    }

    // Insert email container before the button
    messageElement.parentNode?.insertBefore(emailContainer, messageElement) ||
      modalContent.appendChild(emailContainer)
  }
  // Create button
  const button = document.createElement('button')
  button.id = `${id}-button`
  button.textContent = buttonText || 'Try again'

  // Use friendly blue gradient for invalid_request, otherwise default
  const buttonGradient =
    id === 'invalid_request'
      ? 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)'
      : 'linear-gradient(135deg, #4A7BA7 0%, #3d6a94 100%)'
  const buttonShadow =
    id === 'invalid_request'
      ? '0 4px 12px rgba(59, 130, 246, 0.3)'
      : '0 4px 12px rgba(74, 123, 167, 0.25)'
  const buttonShadowHover =
    id === 'invalid_request'
      ? '0 6px 16px rgba(59, 130, 246, 0.4)'
      : '0 6px 16px rgba(74, 123, 167, 0.35)'

  Object.assign(button.style, {
    background: buttonGradient,
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    padding: '0.875rem 2rem',
    fontSize: '0.9375rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: buttonShadow,
    fontFamily: 'inherit',
    letterSpacing: '0.01em',
  })

  // Button hover effect
  button.addEventListener('mouseenter', () => {
    button.style.transform = 'translateY(-1px)'
    button.style.boxShadow = buttonShadowHover
  })

  button.addEventListener('mouseleave', () => {
    button.style.transform = 'translateY(0)'
    button.style.boxShadow = buttonShadow
  })

  button.addEventListener('mousedown', () => {
    button.style.transform = 'translateY(0) scale(0.98)'
  })

  button.addEventListener('mouseup', () => {
    button.style.transform = 'translateY(-1px) scale(1)'
  })

  // Button click handler
  button.onclick = () => {
    if (id === 'access_denied') {
      // window.location.href = `mailto:${email}?subject=Access%20Request%20for%20${title}`
      window.localStorage.clear()
      window.location.href = '/'
      window.location.search = ''
    } else if (id === 'invalid_request') {
      // Navigate to home page for invalid_request
      window.location.href = '/'
      window.location.search = ''
    } else {
      window.location.reload()
    }
  }

  // Assemble modal
  modalContent.appendChild(titleElement)
  modalContent.appendChild(messageElement)
  modalContent.appendChild(button)
  modal.appendChild(modalContent)

  // Close on overlay click
  modal.addEventListener('click', e => {
    if (e.target === modal) {
      modal.style.opacity = '0'
      modalContent.style.transform = 'scale(0.95)'
      setTimeout(() => modal.remove(), 200)
    }
  })

  // Add to DOM and animate in
  document.body.appendChild(modal)

  // Trigger animation
  requestAnimationFrame(() => {
    modal.style.opacity = '1'
    modalContent.style.transform = 'scale(1)'
  })
}
